import React from "react";
import { formatAmountByCurrency, getCurrencySymbol } from "../../utils/currency";
import { useControlCash } from "../../store/controlCashStore";

const EMPTY_TOTALS = {
  income: 0,
  totalPaid: 0,
  totalPending: 0,
  totalCommitted: 0,
  savings: 0,
  availableRemaining: 0,
  finalRemaining: 0
};

/**
 * Componente para mostrar totales separados por moneda con diseño mejorado.
 * Muestra todas las monedas habilitadas en la configuración (incluidas las sin transacciones).
 */
export const CurrencyTotals = ({ totalsByCurrency, exchangeRates = {}, baseCurrency = "PEN" }) => {
  const { state } = useControlCash();
  const hideValue = !state.showAmounts;
  const enabledCurrencies = state.enabledCurrencies || ["PEN", "USD"];
  const dataCurrencies = Object.keys(totalsByCurrency || {});
  const currencies = [...new Set([...enabledCurrencies, ...dataCurrencies])].sort();
  
  if (currencies.length === 0) {
    return (
      <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 text-center text-gray-500">
        No hay datos disponibles
      </div>
    );
  }

  // Colores por moneda
  const currencyColors = {
    PEN: "from-emerald-500 to-teal-600 border-emerald-400/20",
    USD: "from-blue-500 to-indigo-600 border-blue-400/20",
    COP: "from-yellow-500 to-orange-600 border-yellow-400/20",
    ARS: "from-sky-500 to-cyan-600 border-sky-400/20",
    CLP: "from-red-500 to-pink-600 border-red-400/20",
    VEF: "from-purple-500 to-violet-600 border-purple-400/20"
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {currencies.map((currency) => {
        const totals = (totalsByCurrency || {})[currency] || EMPTY_TOTALS;
        const balance = totals.availableRemaining;
        const isPositive = balance >= 0;
        const gradientClass = currencyColors[currency] || "from-gray-500 to-gray-600 border-gray-400/20";
        const currencySymbol = getCurrencySymbol(currency);
        const currencyLabel = currency === "PEN" ? "Soles (Perú)" 
          : currency === "USD" ? "Dólares (USD)"
          : currency === "VEF" ? "Bolívares (Venezuela)"
          : currency === "COP" ? "Pesos (Colombia)"
          : currency === "ARS" ? "Pesos (Argentina)"
          : currency === "CLP" ? "Pesos (Chile)"
          : currency;

        return (
          <div
            key={currency}
            className={`bg-gradient-to-br ${gradientClass} rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all border`}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <span className="text-xl font-bold text-white">
                    {currencySymbol}
                  </span>
                </div>
                <div>
                  <p className="text-sm font-medium text-white/90 uppercase tracking-wide">
                    {currencyLabel}
                  </p>
                </div>
              </div>
            </div>

            {/* Balance Principal */}
            <div className="mb-4">
              <p className="text-xs text-white/70 mb-1">Balance Disponible</p>
              <p className={`text-3xl md:text-4xl font-bold text-white ${
                !isPositive ? "line-through opacity-75" : ""
              }`}>
                {formatAmountByCurrency(Math.abs(balance), currency, hideValue)}
              </p>
              {!isPositive && (
                <p className="text-sm text-white/90 font-semibold mt-1">
                  Déficit: {formatAmountByCurrency(Math.abs(balance), currency, hideValue)}
                </p>
              )}
            </div>

            {/* Breakdown */}
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 space-y-2">
              <div className="flex justify-between items-center text-xs">
                <span className="text-white/80">Ingresos:</span>
                <span className="font-semibold text-white">{formatAmountByCurrency(totals.income, currency, hideValue)}</span>
              </div>
              <div className="flex justify-between items-center text-xs">
                <span className="text-white/80">Gastos:</span>
                <span className="font-semibold text-white">{formatAmountByCurrency(totals.totalCommitted, currency, hideValue)}</span>
              </div>
              {totals.totalPaid > 0 || totals.totalPending > 0 ? (
                <div className="text-xs text-white/70 pl-2 border-l-2 border-white/20">
                  <div className="flex justify-between">
                    <span>Pagado:</span>
                    <span>{formatAmountByCurrency(totals.totalPaid, currency, hideValue)}</span>
                  </div>
                  {totals.totalPending > 0 && (
                    <div className="flex justify-between">
                      <span>Pendiente:</span>
                      <span>{formatAmountByCurrency(totals.totalPending, currency, hideValue)}</span>
                    </div>
                  )}
                </div>
              ) : null}
              {totals.savings > 0 && (
                <div className="flex justify-between items-center text-xs">
                  <span className="text-white/80">Ahorro:</span>
                  <span className="font-semibold text-white">{formatAmountByCurrency(totals.savings, currency, hideValue)}</span>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

/**
 * Componente para mostrar total estimado en moneda base
 */
export const EstimatedTotal = ({ totalsEstimated, baseCurrency = "PEN" }) => {
  const { state } = useControlCash();
  const hideValue = !state.showAmounts;
  
  if (!totalsEstimated) return null;

  return (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl border-2 border-blue-200 dark:border-blue-800 p-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold text-gray-800 dark:text-gray-100">
          Total estimado ({baseCurrency === "PEN" ? "Soles" : baseCurrency === "USD" ? "Dólares" : baseCurrency === "VEF" ? "Bolívares" : baseCurrency})
        </h3>
        <span className="text-xs text-gray-600 dark:text-gray-400">Aproximado</span>
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <div>
          <div className="text-gray-600 dark:text-gray-400 text-xs mb-1">Ingresos</div>
          <div className="font-bold text-green-700 dark:text-green-400">
            {formatAmountByCurrency(totalsEstimated.income, baseCurrency, hideValue)}
          </div>
        </div>
        <div>
          <div className="text-gray-600 dark:text-gray-400 text-xs mb-1">Gastos</div>
          <div className="font-bold text-red-700 dark:text-red-400">
            {formatAmountByCurrency(totalsEstimated.totalCommitted, baseCurrency, hideValue)}
          </div>
        </div>
        <div>
          <div className="text-gray-600 dark:text-gray-400 text-xs mb-1">Ahorro</div>
          <div className="font-bold text-orange-700 dark:text-orange-400">
            {formatAmountByCurrency(totalsEstimated.savings, baseCurrency, hideValue)}
          </div>
        </div>
        <div>
          <div className="text-gray-600 dark:text-gray-400 text-xs mb-1">Balance</div>
          <div className={`font-bold ${totalsEstimated.availableRemaining >= 0 ? "text-blue-700 dark:text-blue-400" : "text-red-700 dark:text-red-400"}`}>
            {formatAmountByCurrency(totalsEstimated.availableRemaining, baseCurrency, hideValue)}
          </div>
        </div>
      </div>
    </div>
  );
};
